import React from 'react';
import { Text } from '@chatsdet/ui';
import { SectionLabel } from '../../cards/components/SectionLabel';
import { useAuth } from '../../../contexts/AuthContext';
import {
  usePageSkillsExperiment,
  type PageSkillsContinuousMetric,
  type PageSkillsExperimentArmAnalysis,
  type PageSkillsRateMetric,
} from '../hooks/useProductCardMetrics';

const windows = [7, 30, 90] as const;

const percentage = (metric: PageSkillsRateMetric) =>
  metric.rate === null ? '—' : `${Math.round(metric.rate * 100)}%`;

const continuous = (metric: PageSkillsContinuousMetric) =>
  metric.mean === null
    ? '—'
    : `${metric.mean.toFixed(1)} ± ${(metric.sd ?? 0).toFixed(1)} (n=${metric.n})`;

function ArmCard({ label, arm }: { label: string; arm: PageSkillsExperimentArmAnalysis }) {
  return (
    <div className="rounded border border-border/70 p-3 text-xs">
      <div className="font-medium">{label}</div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">Exposed agent steps</dt>
        <dd>{arm.exposureMatchedStepCount}</dd>
        <dt className="text-muted-foreground">Turn-log coverage</dt>
        <dd>
          {percentage(arm.turnLogCoverage)} ({arm.turnLogCoverage.positive}/{arm.turnLogCoverage.n})
        </dd>
        <dt className="text-muted-foreground">Turns / run</dt>
        <dd>{continuous(arm.turnsPerRun)}</dd>
        <dt className="text-muted-foreground">Tokens / run</dt>
        <dd>{continuous(arm.tokensPerRun)}</dd>
        <dt className="text-muted-foreground">Step success</dt>
        <dd>
          {percentage(arm.stepSuccess)} ({arm.stepSuccess.n})
        </dd>
        <dt className="text-muted-foreground">Run success guardrail</dt>
        <dd>
          {percentage(arm.guardrails.runSuccess)} ({arm.guardrails.runSuccess.n})
        </dd>
        <dt className="text-muted-foreground">Cache-hit guardrail</dt>
        <dd>
          {percentage(arm.guardrails.cacheHitRate)} ({arm.guardrails.cacheHitRate.n})
        </dd>
      </dl>
    </div>
  );
}

export function ProductItemPageSkillsExperimentSection({
  productItemId,
}: {
  productItemId: string;
}) {
  const { user } = useAuth();
  const [windowDays, setWindowDays] = React.useState<(typeof windows)[number]>(30);
  const query = usePageSkillsExperiment(productItemId, windowDays, user?.isSuperadmin === true);
  const analysis = query.data;

  if (user?.isSuperadmin !== true || (analysis && !analysis.applicable)) return null;

  return (
    <section className="space-y-3 border-t border-border pt-6">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel>Page Skills experiment</SectionLabel>
        <select
          aria-label="Page Skills experiment window"
          className="rounded border border-border bg-background px-2 py-1 text-xs"
          value={windowDays}
          onChange={(event) =>
            setWindowDays(Number(event.target.value) as (typeof windows)[number])
          }
        >
          {windows.map((days) => (
            <option key={days} value={days}>
              {days} days
            </option>
          ))}
        </select>
      </div>
      <Text size="xs" className="text-muted-foreground">
        Primary metrics use exposure-matched miss/fallback steps and per-run means. Turn logs are
        best-effort; coverage is diagnostic. Run success and cache-hit guardrails include all
        experiment steps.
      </Text>
      {query.isLoading ? (
        <Text size="xs" className="text-muted-foreground">
          Loading experiment…
        </Text>
      ) : null}
      {query.isError ? (
        <Text size="xs" className="text-danger">
          Could not load the Page Skills experiment.
        </Text>
      ) : null}
      {analysis ? (
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <ArmCard label="Treatment" arm={analysis.arms.treatment} />
          <ArmCard label="Control" arm={analysis.arms.control} />
        </div>
      ) : null}
    </section>
  );
}
